<script setup lang="ts">
import BootstrapAlert from '@/components/bootstrap/BootstrapAlert.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import SubmitButtonContent from '@/components/devextreme/SubmitButtonContent.vue';
import Routes from '@/constants/Routes';
import { t } from '@/i18n';
import { useAlertStore } from '@/services/stores/alertStore';
import type { IForgotPasswordRequest } from '@/types/interfaces/LoginTypes';
import Icons from '@/constants/Icons';
import { DxButtonItem, DxButtonOptions, DxEmailRule, DxForm, DxGroupItem, DxRequiredRule, DxSimpleItem } from 'devextreme-vue/form';
import { ref } from 'vue';
import { useLoginService } from '@/services/api/loginService';
import { useRouter } from 'vue-router';

definePage({
  name: 'LoginForgotPassword',
  meta: {
    anonymous: true
  }
});

const router = useRouter();
const api = useLoginService();
const alert = useAlertStore();
const loading = ref<boolean>(false);
const email = ref<IForgotPasswordRequest>({
  emailAddress: ''
});

async function onSubmit(): Promise<void> {
  loading.value = true;

  const response = await api.forgotPassword(email.value);
  if (!response.success) {
    alert.showError(response.problem?.detail ?? t('login.forgotPasswordFailed'));
    loading.value = false;
  } else {
    await router.push({ name: Routes.Login.Root });
    alert.showSuccess(t('login.resetEmailSent'));
  }
}
</script>

<template>
  <p class="lead text-center">
    {{ $t('login.enterEmail') }}
  </p>

  <BootstrapAlert type="info" class="mb-3">
    <i18n-t keypath="login.resetLinkExternal" scope="global">
      <template #not><strong>{{ $t('login.not') }}</strong></template>
    </i18n-t>
  </BootstrapAlert>

  <form @submit.prevent="onSubmit">
    <DxForm :disabled="loading" :form-data="email" label-mode="floating">
      <DxSimpleItem :editor-options="{ mode: 'email' }" data-field="emailAddress">
        <DxEmailRule :message="$t('validation.invalidFormat', { field: $t('fields.email') })" />
        <DxRequiredRule :message="$t('validation.required', { field: $t('fields.emailAddress') })" />
      </DxSimpleItem>

      <DxGroupItem :col-count="2">
        <DxButtonItem>
          <DxButtonOptions :use-submit-behavior="true" template="sendButtonTemplate" type="primary" width="100%" />
        </DxButtonItem>
        <DxButtonItem>
          <DxButtonOptions template="cancelButtonTemplate" type="secondary" width="100%" />
        </DxButtonItem>
      </DxGroupItem>

      <template #sendButtonTemplate>
        <SubmitButtonContent :icon="Icons.EnvelopeOpenText" :loading="loading" :text="$t('login.sendResetLink')" />
      </template>

      <template #cancelButtonTemplate>
        <ButtonContent :icon="Icons.Ban" :to="{ name: Routes.Login.Root }" :text="$t('common.cancel')" />
      </template>
    </DxForm>
  </form>
</template>

<style scoped>

</style>